import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Swal from 'sweetalert2';

import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { resolveApiBaseList } from '../utils/apiBase';

const camposIniciales = {
	idUsuario: '',
	tipoUsuario: 'Directora',
	nombre: '',
	apellidoPaterno: '',
	apellidoMaterno: '',
	sexo: 'F',
	telefono: '',
	correo: '',
	fechaNacimiento: ''
};

const obtenerIdUsuarioSesion = () =>
	sessionStorage.getItem('idUsuario') || localStorage.getItem('idUsuario');

const ConsultarInformacionDirectora = () => {
	const [datos, setDatos] = useState(camposIniciales);
	const [loading, setLoading] = useState(true);
	const [isModified, setIsModified] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [passwordData, setPasswordData] = useState({
		contrasenaActual: '',
		nuevaContrasena: '',
		confirmarContrasena: ''
	});
	const [showPasswords, setShowPasswords] = useState({
		actual: false,
		nueva: false,
		confirmar: false
	});
	const contrasenasCoinciden =
		passwordData.nuevaContrasena.trim() === passwordData.confirmarContrasena.trim();

	const apiBases = useMemo(() => resolveApiBaseList(), []);

	const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
		let lastError = null;
		for (const base of apiBases) {
			const normalizedBase = base.replace(/\/$/, '');
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			const url = `${normalizedBase}${normalizedPath}`;
			try {
				const response = await fetch(url, options);
				const text = await response.text();

				if (!response.ok) {
					let message = text || `Error ${response.status}`;
					try {
						const parsed = text ? JSON.parse(text) : null;
						if (parsed && typeof parsed === 'object') {
							message = parsed.message || parsed.error || message;
						}
					} catch (parseErr) {
						// keep raw message
					}
					const error = new Error(message);
					error.status = response.status;
					lastError = error;
					console.warn(`⚠️ Falló la petición a ${url}:`, error);
					continue;
				}

				const contentType = response.headers.get('content-type') || '';
				const trimmed = text.trim();
				if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
					return trimmed ? JSON.parse(trimmed) : {};
				}

				lastError = new Error(`Respuesta inválida desde ${url}`);
				console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
			} catch (error) {
				lastError = error;
				console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
			}
		}

		throw lastError || new Error('No se pudo contactar al servidor.');
	}, [apiBases]);

	const sendJsonWithFallback = useCallback(async (path, payload, method = 'POST') => {
		let lastError = null;
		for (const base of apiBases) {
			const normalizedBase = base.replace(/\/$/, '');
			const normalizedPath = path.startsWith('/') ? path : `/${path}`;
			const url = `${normalizedBase}${normalizedPath}`;
			try {
				const response = await fetch(url, {
					method,
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(payload)
				});
				const text = await response.text();

				if (!response.ok) {
					let message = text || `Error ${response.status}`;
					try {
						const parsed = text ? JSON.parse(text) : null;
						if (parsed && typeof parsed === 'object') {
							message = parsed.message || parsed.error || message;
						}
					} catch (parseErr) {
						// keep raw message
					}
					const error = new Error(message);
					error.status = response.status;
					lastError = error;
					console.warn(`⚠️ Falló la solicitud ${method} a ${url}:`, error);
					continue;
				}

				const trimmed = text.trim();
				if (trimmed && trimmed.startsWith('<')) {
					const error = new Error(`Respuesta inválida desde ${url}`);
					lastError = error;
					console.warn(`⚠️ Contenido no válido desde ${url}`);
					continue;
				}

				if (!trimmed) return { text: '', parsed: null, url };

				if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
					try {
						const parsed = JSON.parse(trimmed);
						return { text: parsed?.mensaje || parsed?.message || trimmed, parsed, url };
					} catch (parseErr) {
						console.warn(`⚠️ No se pudo parsear respuesta JSON desde ${url}:`, parseErr);
					}
				}

				return { text: trimmed, parsed: null, url };
			} catch (error) {
				lastError = error;
				console.warn(`⚠️ Error de red al enviar ${method} a ${url}:`, error);
			}
		}

		throw lastError || new Error('No se pudo contactar al servidor.');
	}, [apiBases]);

	const sanitizeTelefono = useCallback((telefono = '') => telefono.toString().replace(/\D/g, '').slice(0, 10), []);

	const cargarDatos = useCallback(async () => {
		try {
			setLoading(true);

			const idUsuarioSesion = obtenerIdUsuarioSesion();
			if (!idUsuarioSesion) {
				Swal.fire({
					title: 'Sesión no encontrada',
					text: 'No se encontró información de sesión. Por favor, inicia sesión nuevamente.',
					icon: 'error'
				});
				return;
			}

			const cuerpo = await fetchJsonWithFallback(`/directora?idUsuario=${Number(idUsuarioSesion)}`);
			if (cuerpo?.success && cuerpo.directora) {
				const info = cuerpo.directora;
				setDatos({
					idUsuario: info.idUsuario ?? '',
					tipoUsuario: info.tipoUsuario || 'Directora',
					nombre: info.nombre ?? '',
					apellidoPaterno:
						info.apellidoPaterno?.length
							? info.apellidoPaterno
							: info.usuario ?? '',
					apellidoMaterno: info.apellidoMaterno ?? '',
					sexo: info.sexo || 'F',
					telefono: sanitizeTelefono(info.telefono),
					correo: info.correo ?? '',
					fechaNacimiento: info.fechaNacimiento
						? info.fechaNacimiento.substring(0, 10)
						: ''
				});
				setIsModified(false);
			} else {
				throw new Error('No se encontró información de la directora.');
			}
		} catch (error) {
			console.error('❌ Error al cargar la información de la directora:', error);
			Swal.fire({
				title: 'Error',
				text: error.message || 'Ocurrió un error al cargar la información.',
				icon: 'error'
			});
		} finally {
			setLoading(false);
		}
	}, [fetchJsonWithFallback, sanitizeTelefono]);

	useEffect(() => {
		cargarDatos();
	}, [cargarDatos]);

	const handleInputChange = (evento) => {
		const { name, value } = evento.target;
		setDatos((prev) => ({ ...prev, [name]: value }));
		setIsModified(true);
	};

	const handleTelefonoChange = (evento) => {
		const value = evento.target.value;
		const soloNumeros = value.replace(/\D/g, '').slice(0, 10);
		setDatos((prev) => ({ ...prev, telefono: soloNumeros }));
		setIsModified(true);
	};

	const handleSubmit = async (evento) => {
		evento.preventDefault();

		try {
			if (!isModified) {
				Swal.fire({
					title: 'Sin cambios',
					text: 'No se han realizado modificaciones.',
					icon: 'info'
				});
				return;
			}

			const idUsuarioSesion = obtenerIdUsuarioSesion();
			if (!idUsuarioSesion) {
				Swal.fire({
					title: 'Sesión no encontrada',
					text: 'No se encontró información de sesión. Por favor, inicia sesión nuevamente.',
					icon: 'error'
				});
				return;
			}

			const confirmar = await Swal.fire({
				title: '¿Guardar cambios?',
				text: 'Se actualizará la información de la directora.',
				icon: 'question',
				showCancelButton: true,
				confirmButtonText: 'Sí, guardar',
				cancelButtonText: 'Cancelar'
			});

			if (!confirmar.isConfirmed) return;

			const { parsed: cuerpo } = await sendJsonWithFallback('/directora', {
				idUsuario: Number(idUsuarioSesion),
				nombre: datos.nombre,
				apellidoPaterno: datos.apellidoPaterno,
				apellidoMaterno: datos.apellidoMaterno,
				sexo: datos.sexo,
				telefono: datos.telefono,
				correo: datos.correo,
				fechaNacimiento: datos.fechaNacimiento
			}, 'PUT');

			if (!cuerpo?.success || !cuerpo?.directora) {
				throw new Error(cuerpo?.error || 'No fue posible guardar los cambios.');
			}

			setDatos({
				idUsuario: cuerpo.directora.idUsuario ?? Number(idUsuarioSesion),
				tipoUsuario: cuerpo.directora.tipoUsuario || 'Directora',
				nombre: cuerpo.directora.nombre ?? '',
				apellidoPaterno: cuerpo.directora.apellidoPaterno ?? '',
				apellidoMaterno: cuerpo.directora.apellidoMaterno ?? '',
				sexo: cuerpo.directora.sexo || 'F',
				telefono: sanitizeTelefono(cuerpo.directora.telefono),
				correo: cuerpo.directora.correo ?? '',
				fechaNacimiento: cuerpo.directora.fechaNacimiento
					? cuerpo.directora.fechaNacimiento.substring(0, 10)
					: ''
			});
			setIsModified(false);

			Swal.fire({
				title: '¡Éxito!',
				text: 'Información actualizada correctamente.',
				icon: 'success'
			});
		} catch (error) {
			console.error('❌ Error al actualizar información de la directora:', error);
			Swal.fire({
				title: 'Error',
				text: error.message || 'Ocurrió un error al guardar los cambios.',
				icon: 'error'
			});
		}
	};

	const handlePasswordInputChange = (campo, valor) => {
		setPasswordData((prev) => ({ ...prev, [campo]: valor }));
	};

	const togglePasswordVisibility = (campo) => {
		setShowPasswords((prev) => ({ ...prev, [campo]: !prev[campo] }));
	};

	const abrirModalContrasena = () => {
		setPasswordData({
			contrasenaActual: '',
			nuevaContrasena: '',
			confirmarContrasena: ''
		});
		setShowPasswordModal(true);
	};

	const cambiarContrasena = async () => {
		try {
			const contrasenaActual = passwordData.contrasenaActual.trim();
			const nuevaContrasena = passwordData.nuevaContrasena.trim();
			const confirmarContrasena = passwordData.confirmarContrasena.trim();

			if (!contrasenaActual || !nuevaContrasena || !confirmarContrasena) {
				Swal.fire({
					title: 'Campos incompletos',
					text: 'Todos los campos son obligatorios.',
					icon: 'error'
				});
				return;
			}

			if (!contrasenasCoinciden) {
				Swal.fire({
					title: 'Contraseñas diferentes',
					text: 'Las contraseñas nuevas no coinciden.',
					icon: 'error'
				});
				return;
			}

			const idUsuarioSesion = obtenerIdUsuarioSesion();
			if (!idUsuarioSesion) {
				Swal.fire({
					title: 'Sesión no encontrada',
					text: 'No se encontró información de sesión. Por favor, inicia sesión nuevamente.',
					icon: 'error'
				});
				return;
			}

			const { parsed: cuerpo } = await sendJsonWithFallback('/directora/cambiar-contrasena', {
				idUsuario: Number(idUsuarioSesion),
				contrasenaActual,
				nuevaContrasena
			});

			if (!cuerpo?.success) {
				throw new Error(cuerpo?.error || 'No fue posible cambiar la contraseña.');
			}

			Swal.fire({
				title: '¡Éxito!',
				text: cuerpo.mensaje || 'Contraseña actualizada correctamente.',
				icon: 'success'
			});
			setShowPasswordModal(false);
		} catch (error) {
			console.error('❌ Error al cambiar la contraseña:', error);
			Swal.fire({
				title: 'Error',
				text: error.message || 'Ocurrió un error al cambiar la contraseña.',
				icon: 'error'
			});
		}
	};

	if (loading) {
		return (
			<div className="container py-4 px-4">
				<div className="text-center">
					<div className="spinner-border text-primary" role="status">
						<span className="visually-hidden">Cargando...</span>
					</div>
					<p className="mt-3">Cargando información de la directora...</p>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="container py-4 px-4">
				<div className="d-flex justify-content-between align-items-center mb-4">
					<img src={logoInstituto} alt="Logo Instituto" className="logo" />
					<div className="flex-grow-1 text-center">
						<h4 className="mb-0">Instituto Silviano Carrillo</h4>
						<small className="text-muted fs-5">Información de la Directora</small>
					</div>
					<img src={logoSEP} alt="Logo SEP" className="logo" />
				</div>

				<div className="p-4 bg-light rounded shadow-sm">
					<form className="row g-3" onSubmit={handleSubmit}>
						<div className="col-md-4">
							<label htmlFor="idUsuario" className="form-label">ID Usuario</label>
							<input
								type="text"
								className="form-control"
								id="idUsuario"
								value={datos.idUsuario || ''}
								disabled
							/>
						</div>

						<div className="col-md-4">
							<label htmlFor="tipoUsuario" className="form-label">Tipo de Usuario</label>
							<input
								type="text"
								className="form-control"
								id="tipoUsuario"
								value={datos.tipoUsuario || 'Directora'}
								disabled
							/>
						</div>

						<div className="col-md-4">
							<label htmlFor="nombre" className="form-label">Nombre(s)</label>
							<input
								type="text"
								className="form-control"
								id="nombre"
								name="nombre"
								value={datos.nombre || ''}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div className="col-md-4">
							<label htmlFor="apellidoPaterno" className="form-label">Apellido Paterno (Usuario)</label>
							<input
								type="text"
								className="form-control"
								id="apellidoPaterno"
								name="apellidoPaterno"
								value={datos.apellidoPaterno || ''}
								onChange={handleInputChange}
								required
							/>
						</div>

						<div className="col-md-4">
							<label htmlFor="apellidoMaterno" className="form-label">Apellido Materno</label>
							<input
								type="text"
								className="form-control"
								id="apellidoMaterno"
								name="apellidoMaterno"
								value={datos.apellidoMaterno || ''}
								onChange={handleInputChange}
						/>
						</div>

						<div className="col-md-3">
							<label htmlFor="sexo" className="form-label">Sexo</label>
							<select
								id="sexo"
								className="form-select"
								name="sexo"
								value={datos.sexo || 'F'}
								onChange={handleInputChange}
							>
								<option value="F">Femenino</option>
								<option value="M">Masculino</option>
							</select>
						</div>

						<div className="col-md-3">
							<label htmlFor="fechaNacimiento" className="form-label">Fecha de Nacimiento</label>
							<input
								type="date"
								className="form-control"
								id="fechaNacimiento"
								name="fechaNacimiento"
								value={datos.fechaNacimiento || ''}
								onChange={handleInputChange}
							/>
						</div>

						<div className="col-md-3">
							<label htmlFor="telefono" className="form-label">Teléfono</label>
							<input
								type="text"
								className="form-control"
								id="telefono"
								name="telefono"
								value={datos.telefono || ''}
								onChange={handleTelefonoChange}
								placeholder="Máximo 10 dígitos"
							/>
							<div className="form-text">Solo números, máximo 10 dígitos.</div>
						</div>

						<div className="col-md-3">
							<label htmlFor="correo" className="form-label">Correo</label>
							<input
								type="email"
								className="form-control"
								id="correo"
								name="correo"
								value={datos.correo || ''}
								onChange={handleInputChange}
						/>
						</div>

						<div className="col-12 text-end mt-3">
							<button
								type="button"
								className="btn btn-info px-4 me-2"
								onClick={abrirModalContrasena}
							>
								Cambiar Contraseña
							</button>
							<button
								type="button"
								className="btn btn-secondary px-4 me-2"
								onClick={cargarDatos}
							>
								Restaurar
							</button>
							<button
								type="submit"
								className={`btn px-4 ${isModified ? 'btn-primary' : 'btn-outline-secondary'}`}
								disabled={!isModified}
							>
								{isModified ? 'Guardar Cambios' : 'Sin cambios'}
							</button>
						</div>
					</form>
				</div>
			</div>

			{showPasswordModal && (
				<div className="modal fade show" style={{ display: 'block' }} aria-modal="true" role="dialog">
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">Cambiar Contraseña</h5>
								<button type="button" className="btn-close" onClick={() => setShowPasswordModal(false)} aria-label="Close"></button>
							</div>
							<div className="modal-body">
								<form onSubmit={(e) => e.preventDefault()}>
									<div className="mb-3">
										<label className="form-label">Contraseña Actual</label>
										<div className="input-group">
											<input
												type={showPasswords.actual ? 'text' : 'password'}
												className="form-control"
												value={passwordData.contrasenaActual}
												onChange={(e) => handlePasswordInputChange('contrasenaActual', e.target.value)}
												required
											/>
											<button
												type="button"
												className="btn btn-outline-secondary"
												onClick={() => togglePasswordVisibility('actual')}
											>
												<i className={`fas ${showPasswords.actual ? 'fa-eye-slash' : 'fa-eye'}`}></i>
											</button>
										</div>
									</div>

									<div className="mb-3">
										<label className="form-label">Nueva Contraseña</label>
										<div className="input-group">
											<input
												type={showPasswords.nueva ? 'text' : 'password'}
												className="form-control"
												value={passwordData.nuevaContrasena}
												onChange={(e) => handlePasswordInputChange('nuevaContrasena', e.target.value)}
												required
											/>
											<button
												type="button"
												className="btn btn-outline-secondary"
												onClick={() => togglePasswordVisibility('nueva')}
											>
												<i className={`fas ${showPasswords.nueva ? 'fa-eye-slash' : 'fa-eye'}`}></i>
											</button>
										</div>
									</div>

									<div className="mb-3">
										<label className="form-label">Confirmar Nueva Contraseña</label>
										<div className="input-group">
											<input
												type={showPasswords.confirmar ? 'text' : 'password'}
												className={`form-control ${
												passwordData.confirmarContrasena && !contrasenasCoinciden
													? 'is-invalid'
													: ''
												}`}
												value={passwordData.confirmarContrasena}
												onChange={(e) => handlePasswordInputChange('confirmarContrasena', e.target.value)}
												required
											/>
											<button
												type="button"
												className="btn btn-outline-secondary"
												onClick={() => togglePasswordVisibility('confirmar')}
											>
												<i className={`fas ${showPasswords.confirmar ? 'fa-eye-slash' : 'fa-eye'}`}></i>
											</button>
										</div>
										{passwordData.confirmarContrasena && !contrasenasCoinciden && (
											<div className="invalid-feedback">Las contraseñas no coinciden.</div>
										)}
									</div>
								</form>
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
									Cancelar
								</button>
								<button
									type="button"
									className="btn btn-primary"
									onClick={cambiarContrasena}
									disabled={
										!passwordData.contrasenaActual.trim() ||
										!passwordData.nuevaContrasena.trim() ||
										!passwordData.confirmarContrasena.trim() ||
										!contrasenasCoinciden
									}
								>
									Cambiar Contraseña
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{showPasswordModal && (
				<div
					className="modal-backdrop fade show"
					onClick={() => setShowPasswordModal(false)}
				/>
			)}
		</>
	);
};

export default ConsultarInformacionDirectora;
